// Pantalla intermedia post-pago de los planes PRO y ULTRA — la llamada de capacitación
// incluida. Tras un pago exitoso de Pro o de cualquier Ultra
// (`incluyeLlamadaDeCapacitacion`, ADR-2026-10-06-la-llamada-de-capacitacion-tambien-es-de-pro), el
// "Continuar" del éxito trae acá antes que a cualquier otra cosa: el plan incluye
// una llamada personalizada de onboarding y éste es el momento de agendarla, con
// la suscripción recién hecha y el usuario todavía en el flujo.
//
// Llegan acá los TRES éxitos de pago: el checkout embebido AR (`Checkout`), el
// alta por link (`EnterpriseCheckout`, planes Ultra Nivel N) y la vuelta del
// redirect de Stripe/MP (`SubscriptionSuccess`, que consulta el plan al click).
//
// La oferta de facturación viene DESPUÉS de esta pantalla (pedido explícito):
// el llamador que la tenía en su camino (el checkout AR) manda `conFacturacion`
// en el state y el "Continuar" de acá la encadena; sin eso, directo al estudio.
//
// También se llega desde el banner "Agendá tu llamada de capacitación" de la
// home (`desdeInicio` en el state — ADR-2026-08-25-banner-llamada-capacitacion-ultra).
//
// Es puro front, como `FacturacionOferta`: no emite ni guarda nada; solo rutea.
// Mismo guard también: sin `postPago`/`desdeInicio` en el state (entrada directa
// por URL o un refresh) va al estudio; `?preview` la fuerza para revisar el
// diseño sin pagar. Ver ADR-2026-08-25-llamada-capacitacion-ultra-post-pago.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, CalendarCheck, Sparkles, Video } from 'lucide-react';
import { useUserGroup } from '../hooks/useUserGroup';
import { PantallaPostPago } from '../components/postPago/PantallaPostPago';
import {
  nombreDelPlanConLlamada,
  PARAM_HOME_POST_PAGO,
  resolverHomePostPago,
  rutaConHomePostPago,
} from '../lib/checkoutPlans';

/** Booking page de Google Calendar para agendar la llamada de capacitación. */
const LINK_AGENDA_LLAMADA = 'https://calendar.app.google/VyhCP4bfwcAPWbVH9';

export default function CapacitacionUltra() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDevTester, grupo, plan: planDeLaCache } = useUserGroup();
  // `homeDelLlamador` = el destino final que mandó quien cobró (hoy: el cuestionario del
  // alta por link), y es lo único que se propaga al siguiente salto. `homePath` es adónde
  // sale ESTA pantalla: el del llamador si hay, y si no el estudio por grupo, como
  // siempre. Viaja por la URL para sobrevivir un refresh — esta pantalla manda a Google
  // Calendar y volver puede ser una recarga, y ahí el guard de abajo decide con esto.
  // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
  const { homeDelLlamador, homePath } = resolverHomePostPago(
    searchParams.get(PARAM_HOME_POST_PAGO),
    isDevTester,
    grupo,
  );

  const state = location.state as {
    postPago?: boolean;
    /** El llamador ya decidió que la oferta de facturación sigue después de acá
     *  (hoy: el checkout AR, vía `facturacionVisible`). Viaja decidido y no se
     *  recalcula: quién sabe de país y flags es el punto de entrada. */
    conFacturacion?: boolean;
    /** Entró desde el banner de la home, no desde un pago. */
    desdeInicio?: boolean;
    /** El plan que se acaba de pagar (lo manda quien cobró). */
    plan?: string;
  } | null;
  // Recién pagado, la cache del usuario puede tener todavía el plan anterior: manda el que
  // viene del pago. `?preview&plan=negocio` muestra el copy de un plan sin pagar. Sin plan
  // conocido, el copy no nombra ninguno.
  const planDelPreview = searchParams.get('preview') !== null ? searchParams.get('plan') : null;
  const nombreDelPlan = nombreDelPlanConLlamada(state?.plan ?? planDelPreview ?? planDeLaCache);
  // Abrió la agenda (otra pestaña): al volver, la salida ya no es "más tarde" sino "listo".
  const [agendo, setAgendo] = useState(false);
  const preview = searchParams.get('preview') !== null;
  const puedeVerse =
    preview || state?.postPago === true || state?.desdeInicio === true;
  useEffect(() => {
    if (!puedeVerse) navigate(homePath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!puedeVerse) return null;

  // "Agendar videollamada" abre el calendar en otra pestaña (es un link externo), así
  // que el usuario VUELVE a esta pantalla al terminar y sale por "Continuar".
  const continuar = () => {
    if (state?.conFacturacion) {
      // El destino final se lo lleva el próximo salto. Sin destino esto es
      // '/facturacion-oferta' tal cual, o sea lo de siempre para `/checkout`.
      navigate(rutaConHomePostPago('/facturacion-oferta', homeDelLlamador), {
        replace: true,
        state: { postPago: true },
      });
    } else {
      navigate(homePath, { replace: true });
    }
  };

  return (
    <PantallaPostPago
      icono={<Video className="h-8 w-8" />}
      insignia={nombreDelPlan ? `Incluido en tu plan ${nombreDelPlan}` : 'Incluido en tu plan'}
      titulo={nombreDelPlan ? `Gracias por suscribirte al Plan ${nombreDelPlan}` : 'Gracias por suscribirte a Atlas'}
      texto="Tu plan incluye una videollamada personalizada para que le saques el máximo provecho a Atlas desde el primer día."
      puntos={[
        { icono: <CalendarCheck className="h-4 w-4" />, texto: 'Elegís el día y el horario que te queden cómodos.' },
        { icono: <Video className="h-4 w-4" />, texto: 'Es por videollamada, con alguien del equipo de Atlas.' },
        { icono: <Sparkles className="h-4 w-4" />, texto: 'Te mostramos cómo usar Atlas para tu marca o tu agencia.' },
      ]}
      primaria={{
        label: 'Agendar videollamada',
        href: LINK_AGENDA_LLAMADA,
        externa: true,
        onClick: () => setAgendo(true),
        icono: <ArrowUpRight className="h-4 w-4" />,
      }}
      secundaria={{ label: agendo ? 'Listo, continuar' : 'Ahora no, la agendo después', onClick: continuar }}
      nota="También la podés agendar después desde el aviso de la pantalla de inicio."
    />
  );
}
